import React, { useCallback, useState, useMemo } from 'react';
import {
  faCheckCircle,
  faExclamationCircle,
  faQuestionCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InputLabel } from 'src/web/components/InputLabel/component';
import {
  OL_COLOUR_MELON,
  OL_COLOUR_NAVY_BABY,
  OL_COLOUR_TOMAHTO,
} from 'src/web/components/Style/colours';
import { Button } from 'src/web/components/Buttons/Button/Button';
import { ActionPromiseButton } from 'src/web/components/Buttons/ActionButton/ActionPromiseButton';
import { PortalResourceScheduleInviteMembers } from 'src/resource/PortalResource/types';
import { pNotifyAlert } from 'src/web/components/Pnotify/component';
import { Tooltip } from 'src/web/Tooltip/Tooltip';
import {
  StyledInstitutionTable,
  DownloadTemplateSpan,
  StyledCsvField,
  CsvOptionWrapper,
  DialogBox,
  ErrorFieldMessage,
  ViewTableButton,
  ButtonActionWrapper,
  ErrorText,
  StyledTooltipOverlay,
} from '../styles';
import { AccountCreationOption } from '../AccountCreationOption/component';
import { useScheduleInviteMembersApi } from '../../../hooks';
import {
  successAddTeamMembersSettings,
  successAddLearnersSettings,
} from '../consts';
import {
  handleMembersCsvUpload,
  ParsedUserData,
  InvalidParsedUserData,
} from './helpers';
import { MemberType } from '../../../types';

export interface AddMemberByCsvProps {
  memberType: MemberType;
  institutionPath: string;
  onCsvModeChosen: () => void;
  onCancelClicked: () => void;
}

export const AddMemberByCsv: React.FC<AddMemberByCsvProps> = ({
  memberType,
  institutionPath,
  onCsvModeChosen,
  onCancelClicked,
}) => {
  const [parsedUsersData, setParsedUsersData] = useState<ParsedUserData[]>([]);
  const [invalidUsersData, setInvalidUsersData] = useState<
    InvalidParsedUserData[]
  >([]);
  const [isShowingTable, setIsShowingTable] = useState(false);
  const [accountCreationState, setAccountCreationState] = useState({
    createAccount: false,
    skipInvitationEmail: false,
  });

  const isDataStillInvalid = invalidUsersData.length > 0;
  const isShowingCreateAccountWarn = useMemo(() => {
    // Show warning to the users to fill out all the fullnames if the user decide to create the account
    if (accountCreationState.createAccount) {
      return parsedUsersData.some(({ fullname }) => !fullname);
    }
    return false;
  }, [parsedUsersData, accountCreationState.createAccount]);

  const {
    error: errorInviteMembers,
    scheduleInviteMembers,
  } = useScheduleInviteMembersApi(institutionPath);

  const onAddMembers = useCallback(() => {
    const payload: PortalResourceScheduleInviteMembers['args'] = {
      invites: parsedUsersData.map(
        ({ email, externalId, fullname: fullName, role, userGroups }) => ({
          email,
          externalId,
          fullName,
          role,
          groups: userGroups.join(','),
        })
      ),
      generateAccount: accountCreationState.createAccount,
      sendEmail: !accountCreationState.skipInvitationEmail,
    };
    return scheduleInviteMembers(payload);
  }, [parsedUsersData, accountCreationState, scheduleInviteMembers]);

  const onChangeCsvFile = useCallback(
    async (file: File | null): Promise<void> => {
      onCsvModeChosen();
      const { invalidData, parsedData } = await handleMembersCsvUpload(
        memberType,
        file
      );
      setParsedUsersData(parsedData);
      setInvalidUsersData(invalidData);
    },
    [memberType]
  );

  return (
    <>
      <InputLabel>Or add {memberType} via CSV upload</InputLabel>
      <CsvOptionWrapper>
        <StyledCsvField accept=".csv" onValueChange={onChangeCsvFile} />
        <DownloadTemplateSpan>
          <a
            href={
              memberType === 'team members'
                ? 'https://bit.ly/3mMhlhL'
                : 'https://bit.ly/3x9LHOF'
            }
            download
          >
            Download format template
          </a>
          <Tooltip
            placement="bottom"
            trigger="click"
            overlay={
              <StyledTooltipOverlay>
                Use this template as a formatting reference for your CSV file.
              </StyledTooltipOverlay>
            }
          >
            <FontAwesomeIcon
              icon={faQuestionCircle}
              color={OL_COLOUR_NAVY_BABY}
            />
          </Tooltip>
        </DownloadTemplateSpan>
      </CsvOptionWrapper>
      {isDataStillInvalid ? (
        <DialogBox>
          <span>
            <FontAwesomeIcon
              icon={faExclamationCircle}
              color={OL_COLOUR_TOMAHTO}
            />{' '}
            There are {invalidUsersData.length} rows with issues. Fix the issues
            and upload the file again.
          </span>
        </DialogBox>
      ) : (
        parsedUsersData.length > 0 && (
          <DialogBox>
            <span>
              <FontAwesomeIcon icon={faCheckCircle} color={OL_COLOUR_MELON} />{' '}
              {parsedUsersData.length} {memberType} will be added –{' '}
              <ViewTableButton
                onClick={() => setIsShowingTable((prevValue) => !prevValue)}
              >
                View table
              </ViewTableButton>
            </span>
          </DialogBox>
        )
      )}

      {isDataStillInvalid && (
        <StyledInstitutionTable greyedRightmostColumn cellBordered>
          <thead>
            <tr>
              <th />
              <th>Email</th>
              <th>Name (optional) </th>
              <th>Institution membership ID (optional)</th>
              {memberType === 'team members' && <th>Institution role</th>}
              <th>Institution groups</th>
            </tr>
          </thead>
          <tbody>
            {invalidUsersData.map(
              ({
                email,
                externalId,
                fullname,
                itemIndex,
                role,
                userGroups,
                emailWarn,
                roleWarn,
              }) => (
                <tr key={itemIndex}>
                  <td>{itemIndex}</td>
                  <td>
                    {email}
                    {emailWarn && (
                      <ErrorFieldMessage>
                        <FontAwesomeIcon
                          icon={faExclamationCircle}
                          color={OL_COLOUR_TOMAHTO}
                        />{' '}
                        {emailWarn}
                      </ErrorFieldMessage>
                    )}
                  </td>
                  <td>{fullname}</td>
                  <td>{externalId}</td>
                  {memberType === 'team members' && (
                    <td>
                      {role}
                      {roleWarn && (
                        <ErrorFieldMessage>
                          <FontAwesomeIcon
                            icon={faExclamationCircle}
                            color={OL_COLOUR_TOMAHTO}
                          />{' '}
                          {roleWarn}
                        </ErrorFieldMessage>
                      )}
                    </td>
                  )}
                  <td>{userGroups.join(',')}</td>
                </tr>
              )
            )}
          </tbody>
        </StyledInstitutionTable>
      )}

      {isShowingTable && parsedUsersData.length > 0 && (
        <StyledInstitutionTable greyedRightmostColumn cellBordered>
          <thead>
            <tr>
              <th />
              <th>Email</th>
              <th>Name (optional) </th>
              <th>Institution membership ID (optional)</th>
              {memberType === 'team members' && <th>Institution role</th>}
              <th>Institution groups</th>
            </tr>
          </thead>
          <tbody>
            {parsedUsersData.map(
              ({
                email,
                externalId,
                fullname,
                itemIndex,
                role,
                userGroups,
              }) => (
                <tr key={itemIndex}>
                  <td>{itemIndex}</td>
                  <td>{email}</td>
                  <td>{fullname}</td>
                  <td>{externalId}</td>
                  {memberType === 'team members' && <td>{role}</td>}
                  <td>{userGroups.join(',')}</td>
                </tr>
              )
            )}
          </tbody>
        </StyledInstitutionTable>
      )}
      {!isDataStillInvalid && parsedUsersData.length > 0 && (
        <>
          <AccountCreationOption
            memberType={memberType}
            createAccountValue={accountCreationState.createAccount}
            createAccountError={
              isShowingCreateAccountWarn
                ? `To automatically create accounts, you must provide the full names of each ${
                    memberType === 'team members' ? 'team member' : 'learner'
                  }.`
                : ''
            }
            skipInvitationValue={accountCreationState.skipInvitationEmail}
            onCreateAccountValueChange={() =>
              setAccountCreationState((prev) => ({
                ...prev,
                createAccount: !prev.createAccount,
              }))
            }
            onSkipInvitationValueChange={() =>
              setAccountCreationState((prev) => ({
                ...prev,
                skipInvitationEmail: !prev.skipInvitationEmail,
              }))
            }
          />
          <ButtonActionWrapper>
            <Button
              onClick={() => {
                onCancelClicked();
                // Reset data
                setParsedUsersData([]);
                setInvalidUsersData([]);
              }}
            >
              Cancel
            </Button>
            <div>
              <ErrorText>{errorInviteMembers}</ErrorText>
              <ActionPromiseButton
                colour="kelp"
                onClick={onAddMembers}
                onSuccess={() =>
                  pNotifyAlert(
                    memberType === 'team members'
                      ? successAddTeamMembersSettings(parsedUsersData.length)
                      : successAddLearnersSettings(parsedUsersData.length)
                  )
                }
                label={`Add ${memberType}`}
              />
            </div>
          </ButtonActionWrapper>
        </>
      )}
    </>
  );
};
